import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import DevicesPage from "./pages/DevicesPage";
import SAVCasesPage from "./pages/SAVCasesPage";
import SAVCaseDetailPage from "./pages/SAVCaseDetailPage";
import QuotesPage from "./pages/QuotesPage";
import PartsPage from "./pages/PartsPage";
import ProductsPage from "./pages/ProductsPage";
import SalesPage from "./pages/SalesPage";
import POSPage from "./pages/POSPage";
import StatisticsPage from "./pages/StatisticsPage";
import CommissionsPage from "./pages/CommissionsPage";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import BoutiquePage from "./pages/BoutiquePage";
import LandingPage from "./pages/LandingPage";
import DOMErrorBoundary from "./components/DOMErrorBoundary";

// Layout
import MainLayout from "./components/layout/MainLayout";
import ClientPortalLayout from "./components/layout/ClientPortalLayout";

// Client Portal Pages
import ClientPortalDashboard from "./pages/portal/ClientPortalDashboard";
import ClientPortalSAVCases from "./pages/portal/ClientPortalSAVCases";
import ClientPortalSAVDetail from "./pages/portal/ClientPortalSAVDetail";
import ClientPortalQuotes from "./pages/portal/ClientPortalQuotes";
import ClientPortalDevices from "./pages/portal/ClientPortalDevices";
import ClientPortalBonnesAffaires from "./pages/portal/ClientPortalBonnesAffaires";
import ClientPortalBoutique from "./pages/portal/ClientPortalBoutique";
import ClientPortalBooks from "./pages/portal/ClientPortalBooks";
import BookProposalsPage from "./pages/BookProposalsPage";
import CreditsPage from "./pages/CreditsPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import CGVPage from "./pages/CGVPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
  timeout: 15000
});

// Axios request interceptor - always attach token from localStorage
API.interceptors.request.use((config) => {
  const storedToken = localStorage.getItem("token");
  if (storedToken) {
    config.headers.Authorization = `Bearer ${storedToken}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Axios response interceptor - logout only on 401
  useEffect(() => {
    const interceptorId = API.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => API.interceptors.response.eject(interceptorId);
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await API.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      // Only logout on 401 (expired/invalid token), not on network errors
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await API.post("/auth/login", { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await API.post("/auth/register", data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect clients to portal if accessing back-office
  if (user.role === "client" && !location.pathname.startsWith("/portal")) {
    if (user.account_type === "web") {
      return <Navigate to="/portal/boutique" replace />;
    }
    return <Navigate to="/portal" replace />;
  }

  // Redirect non-clients away from portal
  if (user.role !== "client" && location.pathname.startsWith("/portal")) {
    return <Navigate to="/dashboard" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <DOMErrorBoundary>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/boutique" element={<BoutiquePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/cgv" element={<CGVPage />} />
          
          {/* Protected routes - Back-office */}
          <Route path="/" element={<ProtectedRoute allowedRoles={["admin", "vendeur", "technicien"]}><MainLayout /></ProtectedRoute>}>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="clients" element={
              <ProtectedRoute allowedRoles={["admin", "vendeur"]}><ClientsPage /></ProtectedRoute>
            } />
            <Route path="clients/:id" element={
              <ProtectedRoute allowedRoles={["admin", "vendeur"]}><ClientDetailPage /></ProtectedRoute>
            } />
            <Route path="devices" element={
              <ProtectedRoute allowedRoles={["admin", "vendeur"]}><DevicesPage /></ProtectedRoute>
            } />
            <Route path="sav" element={<SAVCasesPage />} />
            <Route path="sav/:id" element={<SAVCaseDetailPage />} />
            <Route path="quotes" element={<QuotesPage />} />
            <Route path="parts" element={<PartsPage />} />
            <Route path="products" element={
              <ProtectedRoute allowedRoles={["admin", "vendeur"]}><ProductsPage /></ProtectedRoute>
            } />
            <Route path="sales" element={
              <ProtectedRoute allowedRoles={["admin", "vendeur"]}><SalesPage /></ProtectedRoute>
            } />
            <Route path="pos" element={
              <ProtectedRoute allowedRoles={["admin", "vendeur"]}><POSPage /></ProtectedRoute>
            } />
            <Route path="statistics" element={
              <ProtectedRoute allowedRoles={["admin", "vendeur"]}><StatisticsPage /></ProtectedRoute>
            } />
            <Route path="commissions" element={
              <ProtectedRoute allowedRoles={["admin"]}><CommissionsPage /></ProtectedRoute>
            } />
            <Route path="users" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <UsersPage />
              </ProtectedRoute>
            } />
            <Route path="settings" element={
              <ProtectedRoute allowedRoles={["admin"]}><SettingsPage /></ProtectedRoute>
            } />
            <Route path="book-proposals" element={
              <ProtectedRoute allowedRoles={["admin", "vendeur"]}><BookProposalsPage /></ProtectedRoute>
            } />
            <Route path="credits" element={
              <ProtectedRoute allowedRoles={["admin", "vendeur"]}><CreditsPage /></ProtectedRoute>
            } />
          </Route>
          
          {/* Client Portal routes */}
          <Route path="/portal" element={<ProtectedRoute><ClientPortalLayout /></ProtectedRoute>}>
            <Route index element={<ClientPortalDashboard />} />
            <Route path="sav" element={<ClientPortalSAVCases />} />
            <Route path="sav/:id" element={<ClientPortalSAVDetail />} />
            <Route path="quotes" element={<ClientPortalQuotes />} />
            <Route path="devices" element={<ClientPortalDevices />} />
            <Route path="bonnes-affaires" element={<ClientPortalBonnesAffaires />} />
            <Route path="boutique" element={<ClientPortalBoutique />} />
            <Route path="livres" element={<ClientPortalBooks />} />
          </Route>
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </DOMErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
